import { useEffect, useState } from 'react'

type ScrollToBottomParams = {
  isReadyToScroll: boolean
  startScroll: boolean
}

/**
 * Custom React hook to scroll an element with id "scroll-area" to the bottom.
 *
 * @param {ScrollToBottomParams} params
 * @param {boolean} params.isReadyToScroll - When true, prepares the hook to scroll on the next trigger.
 * @param {boolean} params.startScroll - When true and ready, triggers the scroll to bottom.
 * @returns {() => void} scrollToBottom - Function to manually scroll to the bottom of the "scroll-area" element.
 *
 * @example
 * const scrollToBottom = useScrollToBottom({ isReadyToScroll, startScroll })
 * // Call scrollToBottom() to scroll manually if needed.
 */
export function useScrollToBottom({ isReadyToScroll, startScroll }: ScrollToBottomParams): () => void {
  const [readyToScroll, setReadyToScroll] = useState(false)

  useEffect(() => {
    if (isReadyToScroll) {
      setReadyToScroll(true)
    }
  }, [isReadyToScroll])

  useEffect(() => {
    if (readyToScroll && startScroll) {
      scrollToBottom()
      setReadyToScroll(false)
    }
  }, [readyToScroll, startScroll])

  function scrollToBottom() {
    const body = document.getElementById('scroll-area')
    body?.scrollTo({ top: body.scrollHeight, behavior: 'smooth' })
  }

  return scrollToBottom
}
